import React, { useState } from 'react';
import { Bell, CheckCircle2, Users, Star, AlertCircle, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const NOTIFICATIONS = [
  { id: 1, type: 'enroll', title: 'New Enrollment', message: 'Priya Nair enrolled in Generative AI', time: '5 min ago', read: false },
  { id: 2, type: 'review', title: 'New Review', message: 'Aisha Patel left a 5-star review on your course', time: '1 hour ago', read: false },
  { id: 3, type: 'approval', title: 'Course Approved', message: 'Your course "Agentic AI" has been approved by admin', time: '3 hours ago', read: false },
  { id: 4, type: 'alert', title: 'Low Completion Alert', message: '4 students are stuck on Module 3 - consider sending a reminder', time: '1 day ago', read: true },
  { id: 5, type: 'enroll', title: 'New Enrollment', message: 'Marcus Johnson enrolled in Full Stack Development', time: '2 days ago', read: true },
  { id: 6, type: 'review', title: 'New Review', message: 'Sophie Martinez rated Cyber Security 5 stars', time: '3 days ago', read: true },
];

const TYPE_CONFIG = {
  enroll: { icon: Users, color: 'bg-blue-100 text-blue-600' },
  review: { icon: Star, color: 'bg-amber-100 text-amber-600' },
  approval: { icon: CheckCircle2, color: 'bg-green-100 text-green-600' },
  alert: { icon: AlertCircle, color: 'bg-red-100 text-red-600' },
};

export default function InstructorNotifications() {
  const [notifications, setNotifications] = useState(NOTIFICATIONS);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const dismiss = (id) => setNotifications(prev => prev.filter(n => n.id !== id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-lg font-bold">Notifications</h2>
          {unreadCount > 0 && (
            <Badge className="bg-primary text-primary-foreground text-xs">{unreadCount} new</Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" className="text-xs" onClick={markAllRead}>Mark all read</Button>
        )}
      </div>

      <div className="space-y-2">
        <AnimatePresence>
          {notifications.map(n => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.alert;
            const Icon = cfg.icon;
            return (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${n.read ? 'bg-card' : 'bg-primary/3 border-primary/20'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">{n.title}</p>
                    {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{n.message}</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">{n.time}</p>
                </div>
                <button onClick={() => dismiss(n.id)} className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
        {notifications.length === 0 && (
          <div className="text-center py-10 text-muted-foreground">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No notifications</p>
          </div>
        )}
      </div>
    </div>
  );
}